import React, { useState, useRef, useEffect } from "react";
import { supabase } from "../../lib/supabaseClient";
import { useAppSelector } from "../../hooks";
import { useNavigate } from "react-router";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { v4 as uuidv4 } from "uuid";
import { useCreateWatchPartyMutation } from "../../services/api";

type Props = {
  preSelectedFilm?: {
    imdb_id: string;
    title: string;
    type: string;
    primary_image?: string;
  };
  onClose: () => void;
  onSuccess: () => void;
};

const CreateWatchPartyModal: React.FC<Props> = ({ preSelectedFilm, onClose, onSuccess }) => {
  const authUser = useAppSelector((s) => s.auth.user);
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedFilm, setSelectedFilm] = useState<any>(preSelectedFilm || null);
  // Smart default time: next 15-minute interval from now
  const getSmartDefaultTime = () => {
    const now = new Date();
    const minutes = now.getMinutes();
    const remainder = 15 - (minutes % 15);
    const nextInterval = new Date(now.getTime() + remainder * 60000);
    
    // If the calculated time is in the past (e.g. seconds difference), add 15 mins
    if (nextInterval <= now) {
      nextInterval.setMinutes(nextInterval.getMinutes() + 15);
    }
    
    // Reset seconds/ms for clean display
    nextInterval.setSeconds(0);
    nextInterval.setMilliseconds(0);
    
    return nextInterval;
  };

  const [scheduledTime, setScheduledTime] = useState<Date | null>(getSmartDefaultTime());
  const [description, setDescription] = useState("");
  const [maxParticipants, setMaxParticipants] = useState(50);
  const [isPrivate, setIsPrivate] = useState(false);
  const [duration, setDuration] = useState(120); // Default 2 hours (in minutes)
  const [searching, setSearching] = useState(false);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);

  const [createWatchParty, { isLoading: loading }] = useCreateWatchPartyMutation();

  // Search films (autocomplete)
  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      setShowAutocomplete(false);
      return;
    }
    const delaySearch = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await supabase.from("films").select("*").ilike("original_title", `%${searchQuery}%`).limit(8);
        setSearchResults(data || []);
        setShowAutocomplete(!!data && data.length > 0);
      } catch (err) {
        // console.error("Autocomplete error:", err);
      }
      setSearching(false);
    }, 300);
    return () => clearTimeout(delaySearch);
  }, [searchQuery]);

  useEffect(() => {
    setSelectedIndex(-1);
  }, [searchResults]);

  // Manual search (button)
  const handleSearch = async () => {
    if (searchQuery.trim().length < 2) return;
    setSearching(true);
    try {
      const { data } = await supabase.from("films").select("*").ilike("original_title", `%${searchQuery}%`).limit(10);
      setSearchResults(data || []);
      setShowAutocomplete(!!data && data.length > 0);
    } catch (err) {
      // console.error("Search error:", err);
    }
    setSearching(false);
  };

  // Create watch party
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!authUser || !selectedFilm || !scheduledTime) {
      alert("Please fill in all required fields");
      return;
    }

    // Validate time is in the future
    if (scheduledTime <= new Date()) {
      alert("Please select a future date and time");
      return;
    }

    try {
      const partyData = {
        host_user_id: authUser.id,
        imdb_id: selectedFilm.imdb_id,
        title: selectedFilm.title || selectedFilm.original_title,
        type: selectedFilm.type,
        scheduled_time: scheduledTime.toISOString(),
        end_time: new Date(scheduledTime.getTime() + duration * 60000).toISOString(),
        description: description.trim() || null,
        max_participants: maxParticipants,
        status: "upcoming",
        is_private: isPrivate,
        invite_token: isPrivate ? uuidv4() : null,
      };

      const newParty = await createWatchParty(partyData).unwrap();

      // alert("Watch party created successfully!");
      onSuccess();
      onClose();
      // Navigate to the created party
      navigate(`/watch-party/${newParty.id}`);
    } catch (err: any) {
      // console.error("Create error:", err);
      alert(err.message || "Failed to create watch party");
    }
  };

  // Duration options
  const durationOptions = [
    { label: "30 Minutes", value: 30 },
    { label: "45 Minutes", value: 45 },
    { label: "1 Hour", value: 60 },
    { label: "1.5 Hours", value: 90 },
    { label: "2 Hours", value: 120 },
    { label: "2.5 Hours", value: 150 },
    { label: "3 Hours", value: 180 },
    { label: "4 Hours", value: 240 },
    { label: "5 Hours", value: 300 },
    { label: "6 Hours", value: 360 },
    { label: "12 Hours", value: 720 },
    { label: "24 Hours", value: 1440 },
  ];

  // Filter passed times for DatePicker
  const filterPassedTime = (time: Date) => {
    const currentDate = new Date();
    const selectedDate = new Date(time);

    return currentDate.getTime() < selectedDate.getTime();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="bg-gray-900 border-2 border-red-600 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 shadow-[0_0_40px_rgba(220,38,38,0.5)] custom-scrollbar"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">Create Watch Party 🎉</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleCreate} className="space-y-4">
          {/* Film Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              Select Film/Show <span className="text-red-500">*</span>
            </label>

            {selectedFilm ? (
              <div className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg border border-red-600">
                <img
                  src={selectedFilm.primary_image || "https://placehold.co/200x300/1f2937/ffffff/png?text=Image+Not+Found"}
                  alt={selectedFilm.title || selectedFilm.original_title || "Film"}
                  className="w-12 h-16 object-cover rounded"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.onerror = null;
                    target.src = "https://placehold.co/200x300/1f2937/ffffff/png?text=Image+Not+Found";
                  }}
                />
                <div className="flex-1">
                  <h4 className="text-white font-semibold my-0">{selectedFilm.title || selectedFilm.original_title}</h4>
                  <p className="text-sm text-gray-400">
                    {selectedFilm.type === "tvSeries" ? "TV Series" : "Movie"}
                    {selectedFilm.start_year && ` • ${selectedFilm.start_year}`}
                  </p>
                </div>
                {!preSelectedFilm && (
                  <button type="button" onClick={() => setSelectedFilm(null)} className="text-red-500 hover:text-red-400">
                    Change
                  </button>
                )}
              </div>
            ) : (
              <div>
                <div className="flex gap-2 mb-2 relative">
                  <input
                    ref={inputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => searchResults.length > 0 && setShowAutocomplete(true)}
                    onBlur={() => setTimeout(() => setShowAutocomplete(false), 200)}
                    onKeyDown={(e) => {
                      if (e.key === "ArrowDown") {
                        e.preventDefault();
                        setSelectedIndex((prev) => Math.min(prev + 1, searchResults.length - 1));
                      } else if (e.key === "ArrowUp") {
                        e.preventDefault();
                        setSelectedIndex((prev) => Math.max(prev - 1, -1));
                      } else if (e.key === "Enter") {
                        e.preventDefault();
                        if (selectedIndex >= 0 && searchResults[selectedIndex]) {
                          setSelectedFilm(searchResults[selectedIndex]);
                          setSearchResults([]);
                          setSearchQuery("");
                          setShowAutocomplete(false);
                        } else {
                          handleSearch();
                        }
                      }
                    }}
                    placeholder="Search for a film or show..."
                    className="flex-1 px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 focus:border-red-600 focus:outline-none text-white"
                  />
                  <button
                    type="button"
                    onClick={handleSearch}
                    disabled={searching}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                  >
                    {searching ? "..." : "Search"}
                  </button>
                  {/* Autocomplete Dropdown */}
                  {showAutocomplete && searchResults.length > 0 && (
                    <div className="absolute top-full left-0 mt-2 w-full bg-gray-900 border-2 border-red-600 rounded-lg shadow-lg max-h-60 overflow-y-auto z-50">
                      {searchResults.map((film, index) => (
                        <button
                          key={film.id}
                          type="button"
                          onClick={() => {
                            setSelectedFilm(film);
                            setSearchResults([]);
                            setSearchQuery("");
                            setShowAutocomplete(false);
                          }}
                          className={`w-full flex items-center gap-3 p-3 hover:bg-gray-800 transition-colors text-left border-b border-gray-800 last:border-b-0 ${
                            index === selectedIndex ? "bg-gray-700" : ""
                          }`}
                        >
                          <img
                            src={film.primary_image || "https://placehold.co/200x300/1f2937/ffffff/png?text=Image+Not+Found"}
                            alt={film.original_title}
                            className="w-10 h-14 object-cover rounded"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.onerror = null;
                              target.src = "https://placehold.co/200x300/1f2937/ffffff/png?text=Image+Not+Found";
                            }}
                          />
                          <div>
                            <h4 className="text-white font-semibold text-sm">{film.original_title}</h4>
                            <p className="text-xs text-gray-400">
                              {film.type === "tvSeries" ? "TV Series" : "Movie"} • {film.start_year}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>


          {/* Time Inputs - Side by side on larger screens */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Scheduled Time */}
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                Scheduled Time <span className="text-red-500">*</span>
              </label>
              <DatePicker
                selected={scheduledTime}
                onChange={(date) => setScheduledTime(date)}
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={15}
                dateFormat="MMMM d, yyyy HH:mm"
                minDate={new Date()}
                placeholderText="Select start time"
                filterTime={filterPassedTime}
                className="w-full px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 focus:border-red-600 focus:outline-none text-white"
                wrapperClassName="w-full"
              />
              <p className="text-xs text-gray-500 mt-1">Your timezone: {Intl.DateTimeFormat().resolvedOptions().timeZone}</p>
            </div>

            {/* Duration Dropdown */}
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                Duration <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  value={duration}
                  onChange={(e) => setDuration(Number(e.target.value))}
                  className="w-full px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 focus:border-red-600 focus:outline-none text-white appearance-none cursor-pointer"
                >
                  {durationOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                  <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                  </svg>
                </div>
              </div>
              {scheduledTime && (
                <p className="text-xs text-gray-500 mt-1">
                  Ends at: {new Date(scheduledTime.getTime() + duration * 60000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">Description (Optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add details about your watch party..."
              className="w-full px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 focus:border-red-600 focus:outline-none text-white resize-none"
              rows={3}
              maxLength={500}
            />
          </div>


          {/* Max Participants & Private Party - Side by side on larger screens */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Max Participants */}
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">Max Participants</label>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setMaxParticipants(prev => Math.max(2, prev - 1))}
                  className="w-10 h-10 flex items-center justify-center bg-gray-800 border border-gray-700 rounded-lg text-white hover:bg-gray-700 hover:border-red-600 transition-colors"
                >
                  -
                </button>
                <div className="flex-1 relative">
                  <input
                    type="number"
                    value={maxParticipants}
                    onChange={(e) => setMaxParticipants(Math.max(2, Math.min(500, parseInt(e.target.value) || 2)))}
                    min={2}
                    max={500}
                    className="w-full px-4 py-2 text-center rounded-lg bg-gray-800 border border-gray-700 focus:border-red-600 focus:outline-none text-white appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setMaxParticipants(prev => Math.min(500, prev + 1))}
                  className="w-10 h-10 flex items-center justify-center bg-gray-800 border border-gray-700 rounded-lg text-white hover:bg-gray-700 hover:border-red-600 transition-colors"
                >
                  +
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1 text-center">Minimum 2, Maximum 500</p>
            </div>

            {/* Private Party Toggle */}
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">Privacy</label>
              <div className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg border border-gray-700 h-[52px]">
                <div className="flex-1">
                  <h4 className="text-white font-semibold text-sm">Private Party 🔒</h4>
                  <p className="text-xs text-gray-400">Link only</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isPrivate}
                    onChange={(e) => setIsPrivate(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-red-600 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                </label>
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={loading || !selectedFilm || !scheduledTime}
              className="flex-1 py-3 bg-red-600 hover:bg-red-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
            >
              {loading ? "Creating..." : "Create Watch Party"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateWatchPartyModal;
